import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { linksService, type LinkStatus, type UpdateLinkPayload, type Link } from '../services/linksService';

export const useLinks = () => {
  return useQuery({
    queryKey: ['links'],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    queryFn: linksService.getLinks
  });
};

export const useCreateLink = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: linksService.createLink,
    onSuccess: () => {
      // Invalidate and refetch links after successful creation
      queryClient.invalidateQueries({ queryKey: ['links'] });
    }
  });
};

export const useDeleteLink = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: linksService.deleteLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
    }
  });
};

export const useUpdateLinkStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { linkId: string; status: LinkStatus }) => 
      linksService.updateLinkStatus(variables.linkId, variables.status),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
    onError: (error, variables, context) => {
      console.error(`Failed to update link status to ${variables.status} for link ${variables.linkId}:`, error);
    }
  });
};

export const useUpdateLink = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Link, 
    Error, 
    { linkId: string; payload: UpdateLinkPayload },
    unknown 
  >({
    mutationFn: ({ linkId, payload }) => linksService.updateLink(linkId, payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
    onError: (error, variables, context) => {
      console.error(`Failed to update link ${variables.linkId}:`, error);
    }
  });
}; 